import React, { useEffect, useState } from 'react';
import { auditAPI } from '../../lib/api';
import { ROLLEN_NAMEN, rollenName } from '../../lib/rollen';
import { datumKurz, istGesperrt, letzteAnmeldungText, verlaufEintraege } from '../../lib/verwaltungskonten';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { KontoAktionen } from './KontoAktionen';
import { StatusAbzeichen } from './KontoTabelle';

const Fehlt = () => <span className="text-slate-400">noch nicht erfasst</span>;

const Zeile = ({ name, children, title, testid }) => (
  <div className="flex items-start justify-between gap-3 border-t py-2 text-sm first:border-t-0" data-testid={testid}>
    <span className="text-slate-500" title={title}>{name}</span>
    <span className="text-right text-slate-900">{children}</span>
  </div>
);

const Abschnitt = ({ titel, children, testid }) => (
  <section className="space-y-1" data-testid={testid}>
    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{titel}</h3>
    <div className="rounded-lg border px-3">{children}</div>
  </section>
);

const Tag = ({ iso }) => (iso ? datumKurz(iso) : <Fehlt />);

const VerlaufEintrag = ({ eintrag, nr }) => (
  <li className="py-2 text-sm" data-testid={`verlauf-eintrag-${nr}`}>
    <p className={eintrag.rot ? 'font-medium text-red-700' : 'font-medium text-slate-900'}>{eintrag.text}</p>
    <p className="text-xs text-slate-500">{eintrag.wann}{eintrag.wer ? ` · ${eintrag.wer}` : ''}</p>
    {eintrag.detail && <p className="text-xs text-slate-600">{eintrag.detail}</p>}
  </li>
);

const Verlauf = ({ kontoId, version }) => {
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(false);
  const [eintraege, setEintraege] = useState([]);
  useEffect(() => {
    let aktiv = true;
    setLaedt(true);
    setFehler(false);
    auditAPI.get('user', kontoId)
      .then((r) => { if (aktiv) setEintraege(verlaufEintraege(r.data || [], ROLLEN_NAMEN)); })
      .catch(() => { if (aktiv) { setFehler(true); setEintraege([]); } })
      .finally(() => { if (aktiv) setLaedt(false); });
    return () => { aktiv = false; };
  }, [kontoId, version]);
  if (laedt && eintraege.length === 0) return <p className="py-2 text-sm text-slate-500" data-testid="verlauf-laedt">Verlauf wird geladen...</p>;
  if (fehler) return <p className="py-2 text-sm text-red-600" data-testid="verlauf-fehler">Der Verlauf konnte nicht geladen werden.</p>;
  if (eintraege.length === 0) return <p className="py-2 text-sm text-slate-500" data-testid="verlauf-leer">Noch keine Einträge.</p>;
  const liste = eintraege.map((e, i) => <VerlaufEintrag key={`${e.wann}-${i}`} eintrag={e} nr={i} />);
  return <ul className="divide-y" data-testid="verlauf-liste">{liste}</ul>;
};

// Detail-Blatt „Konto-Details“ (rechts; am Handy volle Breite). konto kommt live aus der Liste.
export const KontoDetails = ({ konto, onClose, ich, noAccessMap, onAktion, version }) => (
  <Sheet open={Boolean(konto)} onOpenChange={(o) => { if (!o) onClose(); }}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="konto-details">
      {konto && (
        <div className="space-y-5">
          <SheetHeader className="text-left">
            <SheetTitle className="font-[Manrope]" data-testid="konto-details-name">{konto.first_name} {konto.last_name}</SheetTitle>
            <SheetDescription className="break-all">{konto.email}</SheetDescription>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Badge className="bg-blue-100 text-blue-700">{rollenName(konto.role)}</Badge>
              <StatusAbzeichen konto={konto} ort="blatt" />
            </div>
          </SheetHeader>
          <Abschnitt titel="Anmeldung & Sicherheit" testid="details-sicherheit">
            <Zeile name="Letzte Anmeldung" testid="details-letzte-anmeldung">
              {letzteAnmeldungText(konto.last_login_at, new Date(), noAccessMap.get(konto.id) === true)}
            </Zeile>
            <Zeile name="2FA eingerichtet am" testid="details-2fa">
              {konto.zwei_faktor_aktiv ? (konto.zwei_faktor_seit ? datumKurz(konto.zwei_faktor_seit) : 'Aktiv') : 'Nicht eingerichtet'}
            </Zeile>
            <Zeile name="Passwort zuletzt festgelegt" title="Bei älteren Konten geschätzt" testid="details-passwort"><Tag iso={konto.password_set_at} /></Zeile>
            <Zeile name="Link oder Zugangscode zuletzt gesendet" testid="details-link"><Tag iso={konto.passwort_link_gesendet_am} /></Zeile>
            <Zeile name="Konto erstellt am" testid="details-erstellt"><Tag iso={konto.created_at} /></Zeile>
            {konto.is_active === false && <Zeile name="Archiviert am" testid="details-archiviert"><Tag iso={konto.archived_at} /></Zeile>}
          </Abschnitt>
          {(istGesperrt(konto) || konto.gesperrt_am || konto.entsperrt_am) && (
            <Abschnitt titel="Sperre" testid="details-sperre">
              {(istGesperrt(konto) || konto.gesperrt_am) && (
                <>
                  <Zeile name="Gesperrt am" testid="details-gesperrt-am"><Tag iso={konto.gesperrt_am} /></Zeile>
                  <Zeile name="Gesperrt von" testid="details-gesperrt-von">{konto.gesperrt_von || '—'}</Zeile>
                  <Zeile name="Grund" testid="details-grund">{konto.sperr_grund || '—'}</Zeile>
                </>
              )}
              {konto.entsperrt_am && (
                <>
                  <Zeile name="Zuletzt entsperrt am" testid="details-entsperrt-am">{datumKurz(konto.entsperrt_am)}</Zeile>
                  <Zeile name="Entsperrt von" testid="details-entsperrt-von">{konto.entsperrt_von || '—'}</Zeile>
                </>
              )}
            </Abschnitt>
          )}
          <Abschnitt titel="Verlauf" testid="details-verlauf"><Verlauf kontoId={konto.id} version={version} /></Abschnitt>
          <KontoAktionen konto={konto} ich={ich} onAktion={onAktion} ort="blatt" />
        </div>
      )}
    </SheetContent>
  </Sheet>
);

export default KontoDetails;
